package com.starparty.app.astro0

import com.starparty.app.geometry.SP_Point
import com.starparty.app.vector_maths.Matrix4d
import com.starparty.app.vector_maths.Point3d




/**
 * Title:
 * Description:
 * Copyright:    Copyright (c) 2002
 * Company:
 * @author
 * @version 1.0
 */
class CoordinateTransformer(time: SP_Time, lat: Double, lon: Double) {

    protected val SIDERIAL_DAY: Double
    protected val SIDERIAL_DAY_TO_MS: Double

    protected val MS_TO_EQU_DEG: Double

    protected var itsEquCoords: EquatorialCoords
    protected var itsAltAzmCoords: AltAzimuthCoords
    protected var itsEquTransform = Matrix4d()
    protected var itsAltAzmTransform = Matrix4d()
    var projection: Projection
        protected set
    var tracking = false
    var centerPoint: SP_Point = SP_Point(0.0, -100.0)

    val projectedZenith: SP_Point
        get() = getProjectedAltAzimuthPoint(90.0, 0.0)

    val projectedNadir: SP_Point
        get() = getProjectedAltAzimuthPoint(-90.0, 0.0)

    val projectedNorth: SP_Point
        get() = getProjectedAltAzimuthPoint(0.0, 0.0)

    val projectedSouth: SP_Point
        get() = getProjectedAltAzimuthPoint(0.0, 180.0)

    val projectedEast: SP_Point
        get() = getProjectedAltAzimuthPoint(0.0, 90.0)

    val projectedWest: SP_Point
        get() = getProjectedAltAzimuthPoint(0.0, 270.0)

    fun getTime(): SP_Time {
        return itsAltAzmCoords.location.time
    }

    fun setTime(t:SP_Time) {

        if (tracking === true) {

            itsAltAzmCoords.setTime(t)

        } else {

            val curTime = getTime().siderealTime
            val newTime = t.siderealTime
            val timeInc = newTime - curTime

            itsAltAzmCoords.setTime(t)

            incrementRA(timeInc)
        }
    }

    fun getPlace(): SP_Place {
        return itsAltAzmCoords.location.place
    }

    fun setLat(lat: Double) {
        itsAltAzmCoords.setLat(lat)
    }

    fun setLon(lon: Double) {
        itsAltAzmCoords.setLon(lon)
    }

    fun getProjectedAltAzimuthPoint(alt: Double, azm: Double): SP_Point {
        val p = Point3d()
        val sphPoint = altAzmSphericalToViewSpherical(azm, alt)
        SphericalCoords.sphericalToCartesian(sphPoint.att, sphPoint.elv, p)
        val itsProj = projection.project(p)
        val x = itsProj.x
        val y = itsProj.y
        return SP_Point(x, y)
    }

    /** Sets the center of the screen to the specified
     * equatorial coordinates (in decimal degrees).  */
    fun setEquatorialCoordinatesToMiddle(ra: Double, dec: Double) {
        itsEquCoords.setEquatorialCoordinates(ra, dec)
    }

    /** Sets the viewpoint to look along the specified
     * horizontal coordinates (in decimal degrees) with zenith upwards.  */
    fun setAltAzmCoordinatesToMiddleWithZenithUp(azm: Double, alt: Double) {
        val equNorth = altAzmSphericalToEquatorialSpherical(0.0, 0.0)
        itsEquCoords.setEquatorialCoordinates(equNorth.rA, equNorth.dec)
        incrementAlt(-alt)

        if (itsAltAzmCoords.location.place.latitude < 0)
        {
           itsEquCoords.rotAroundPoint(180.0, 0.0, 0.0)
           incrementAzm(azm)
        }
        else {
           incrementAzm(-azm)
        }
    }

    /** Sets the viewpoint to look along the specified
     * horizontal coordinates (in decimal degrees).  */
    fun setAltAzmCoordinatesToMiddle(azm: Double, alt: Double) {
        val equ = altAzmSphericalToEquatorialSpherical(azm, alt)
        itsEquCoords.setEquatorialCoordinates(equ.rA, equ.dec)
    }

    fun moveEquatorialPointToPoint(
        ra: Double,
        dec: Double,
        to: SP_Point?
    ) {
        val from = itsEquCoords.equSphericalToViewSpherical(ra, dec)
        moveSphericalPointToPoint(from, to)
    }

    fun moveSphericalPointToPoint(from: SphPoint, to: SP_Point?) {
        val angTo = getSphericalCoordinates(to)
        val att = from.att - angTo.att
        val elv = from.elv - angTo.elv

        itsEquCoords.rotY(att + angTo.att)
        itsEquCoords.rotX(-elv)
        itsEquCoords.rotY(-angTo.att)
    }

    fun movePointToPoint(from: SP_Point?, to: SP_Point?) {
        val angFrom = getSphericalCoordinates(from)
        moveSphericalPointToPoint(angFrom, to)
    }

    fun incrementZoomFromCenter(inc: Double) {
        val ec = equSphericalCoordsOfCenterPoint
        incrementZoomFromMiddle(inc)
        setEquatorialCoordinatesOfCenter(ec.rA, ec.dec)
    }

    fun setEquatorialCoordinatesOfCenter(ra: Double, dec: Double) {
        moveEquatorialPointToPoint(ra, dec, centerPoint)
    }

    fun incrementZoomFromMiddle(inc: Double) {
        projection.incrementZoom(inc)
    }

    fun panHorizontal(degrees: Double) {
        itsEquCoords.rotY(degrees)
    }

    fun panVertical(degrees: Double) {
        itsEquCoords.rotX(degrees)
    }

    /** rolls around the moveable center point  */
    fun rollAroundCenter(degrees: Double) {
        val equ = viewSphericalCoordsOfCenterPoint
        itsEquCoords.rotAroundPoint(degrees, equ.att, equ.elv)
    }

    /** rolls around the stationary middle point  */
    fun rollAroundMiddle(degrees: Double) {
        itsEquCoords.rotZ(degrees)
    }

    fun incrementRA(degrees: Double) {
        val vs = itsEquCoords.equCartesianToViewSpherical(0.0, 1.0, 0.0)
        itsEquCoords.rotAroundPoint(degrees, vs.att, vs.elv)
    }

    /** Increments the Declination around the moveable center point  */
    fun incrementDecAroundCenter(degrees: Double) {
        val es = equSphericalCoordsOfCenterPoint
        val ra = es.rA + 90
        val vs = itsEquCoords.equSphericalToViewSpherical(ra, 0.0)
        itsEquCoords.rotAroundPoint(degrees, vs.att, vs.elv)
    }

    /** Increments the Declination around the stationary middle point  */
    fun incrementDecAroundMiddle(degrees: Double) {
        val c = Point3d(0.0, 0.0, 1.0) // Middle point
        val es = itsEquCoords.viewCartesianToEquatorialSpherical(c)
        val ra = es.rA + 90
        val vs = itsEquCoords.equSphericalToViewSpherical(ra, 0.0)
        itsEquCoords.rotAroundPoint(degrees, vs.att, vs.elv)
    }

    val viewSphericalCoordsOfCenterPoint: SphPoint
        get() = getViewSphericalCoordinates(centerPoint)

    fun getViewSphericalCoordinates(p: SP_Point?): SphPoint {
        val viewCart = projection.invertProjection(p)
        val viewSph = SphPoint()
        SphericalCoords.cartesianToSpherical(viewCart, viewSph)
        return viewSph
    }

    val equSphericalCoordsOfCenterPoint: SphPoint
        get() = getEquatorialCoordinates(centerPoint)

    val altAzmSphericalCoordsOfCenterPoint: SphPoint
        get() {
            val es = equSphericalCoordsOfCenterPoint
            return EquSphericalToAltAzmSpherical(es.rA, es.dec)
        }

    /** Increments the Altitude around the moveable center point  */
    fun incrementAltAroundCenter(degrees: Double) {
        val aas = altAzmSphericalCoordsOfCenterPoint
        val azm = aas.azm + 90
        val vs = altAzmSphericalToViewSpherical(azm, 0.0)
        itsEquCoords.rotAroundPoint(degrees, vs.att, vs.elv)
    }

    /** Increments the Altitude around the stationary middle point  */
    fun incrementAltAroundMiddle(degrees: Double) {
        val c = Point3d(0.0, 0.0, 1.0)
        val aas = viewCartesianToAltAzmSpherical(c)
        val azm = aas.azm + 90
        val vs = altAzmSphericalToViewSpherical(azm, 0.0)
        itsEquCoords.rotAroundPoint(degrees, vs.att, vs.elv)
    }

    fun incrementAlt(degrees: Double) {
        val vs = altAzmCartesianToViewSpherical(1.0, 0.0, 0.0)
        itsEquCoords.rotAroundPoint(degrees, vs.att, vs.elv)
    }

    fun incrementAzm(degrees: Double) {
        val vs = altAzmCartesianToViewSpherical(0.0, 1.0, 0.0)
        itsEquCoords.rotAroundPoint(degrees, vs.att, vs.elv)
    }

    fun EquSphericalToAltAzmSpherical(ra: Double, dec: Double): SphPoint {
        return itsAltAzmCoords.EquSphericalToAltAzmSpherical(ra, dec)
    }

    protected fun viewCartesianToAltAzmSpherical(c: Point3d?): SphPoint {
        val sph = SphPoint()
        viewCartesianToAltAzmCartesian(c)
        SphericalCoords.cartesianToSpherical(c, sph)
        return sph
    }

    protected fun viewCartesianToAltAzmCartesian(c: Point3d?) {
        itsEquCoords.invertRotateCartesian(c!!)
        itsAltAzmCoords.invertRotateCartesian(c)
    }

    protected fun altAzmSphericalToViewSpherical(azm: Double, alt: Double): SphPoint {
        val cart = Point3d()
        SphericalCoords.sphericalToCartesian(azm, alt, cart)
        return altAzmCartesianToViewSpherical(cart.x, cart.y, cart.z)
    }

    fun altAzmSphericalToEquatorialSpherical(azm: Double, alt: Double): SphPoint {
        val cart = Point3d()
        SphericalCoords.sphericalToCartesian(azm, alt, cart)
        return altAzmCartesianToEquatorialSpherical(cart.x, cart.y, cart.z)
    }

    protected fun altAzmCartesianToEquatorialSpherical(
        x: Double,
        y: Double,
        z: Double
    ): SphPoint {
        val sph = SphPoint()
        val cart = altAzmCartesianToEquatorialCartesian(x, y, z)
        SphericalCoords.cartesianToSpherical(cart, sph)
        return sph
    }

    protected fun altAzmCartesianToEquatorialCartesian(
        x: Double,
        y: Double,
        z: Double
    ): Point3d {
        val cart = Point3d(x, y, z)
        itsAltAzmCoords.applyRotation(cart)
        return cart
    }

    protected fun altAzmCartesianToViewSpherical(
        x: Double,
        y: Double,
        z: Double
    ): SphPoint {
        val sph = SphPoint()
        val cart = altAzmCartesianToViewCartesian(x, y, z)
        SphericalCoords.Companion.cartesianToSpherical(cart, sph)
        return sph
    }

    protected fun altAzmCartesianToViewCartesian(
        x: Double,
        y: Double,
        z: Double
    ): Point3d {
        val cart = Point3d(x, y, z)
        itsAltAzmCoords.applyRotation(cart)
        itsEquCoords.applyRotation(cart)
        return cart
    }

    fun getSphericalCoordinates(p: SP_Point?): SphPoint {
        val cart = projection.invertProjection(p)
        val ang = SphPoint()
        SphericalCoords.cartesianToSpherical(cart, ang)
        return ang
    }

    fun getEquatorialCoordinates(p: SP_Point?): SphPoint {
        val c = projection.invertProjection(p)
        return itsEquCoords.viewCartesianToEquatorialSpherical(c)
    }

    val equatorialTransform: Matrix4d
        get() {
            createEquatorialTransform()
            return itsEquTransform
        }


    val altAzmTransform: Matrix4d
        get() {
            createAltAzimuthTransform()
            return itsAltAzmTransform
        }

    protected fun createAltAzimuthTransform() {
        itsAltAzmTransform.set(itsAltAzmCoords.itsRotation)
        itsAltAzmTransform.mul(itsEquCoords.itsRotation)
        itsAltAzmTransform.mul(projection.matrix)
    }

    protected fun createEquatorialTransform() {
        itsEquTransform.set(itsEquCoords.itsRotation)
        itsEquTransform.mul(projection.matrix)
    }

    init {

        SIDERIAL_DAY = UMS.unitMinSecToDecimal("23 56 00")

        SIDERIAL_DAY_TO_MS = SIDERIAL_DAY * SP_Time.HRS_TO_MS()
        MS_TO_EQU_DEG = 360.0 / SIDERIAL_DAY_TO_MS

        val equRot = Matrix4d()
        equRot.setIdentity()
        itsEquCoords = EquatorialCoords(equRot)
        itsAltAzmCoords = AltAzimuthCoords(Location(time, lat, lon))
        projection = SteriographicProjection()
    }
}